// Reclamos (Claims) — its own tab in the Reports/Commissions group (next to
// Policy Mix). Presented as an EXPANDABLE CLIENT CARD per policy (Lissette
// 2026-07-10: "tipo ficha desplegable del cliente, con campos personalizados"):
// a collapsed header shows the client + carrier + status, and expanding reveals
// a clean ficha with all the derived fields grouped, the editable claim fields,
// and agency-shared custom fields.
//
// Everything shown is DERIVED from the policy card + primary client info the
// agency already has — nothing here needs a new table. The editable bits
// (NPN on application, ACA portal, claim comments, and the custom fields) are
// stored on the policy's `customData` JSON blob via the payments custom-data
// endpoint (merged server-side, so writing one key never wipes another and NO
// migration is needed). Custom-field NAMES are shared agency-wide implicitly:
// the set of fields = the union of every `claim_cf_*` key present across the
// loaded policies, so a field added on one claim shows (empty) on all of them.
//
// The "Comisión" status is about COMMISSIONS ONLY, never the client's monthly
// premium (Lissette 2026-07-13: "en reclamos solamente es las comisiones no
// pagas, no lo que el cliente no pagó"). A policy's client shows "Comisión
// pagada" when they have a received commission this year (parent passes the set
// of such client ids from the commission tracker), else "Comisión no pagada".
import { useMemo, useState, useEffect } from 'react';
import { FileText, Download, ChevronDown, ChevronRight, Plus, ExternalLink, Copy, Check } from 'lucide-react';
import { Select } from './ui.jsx';
import { toast } from './Toast.jsx';
import { useMenciones } from './MenuDeMenciones.jsx';
import { fmt, formatClientName } from '../utils.js';
import { POLICY_TYPES, getPolicyType } from '../policyTypes.js';
import { savePaymentCustomData, getClient } from '../api.js';

// customData keys we own on the policy blob. Namespaced so they never collide
// with the Monthly-Payments custom columns (whose ids are generated) and merged
// server-side so saving one never wipes the others.
const NPN_KEY = 'claim_npn';
const ACA_KEY = 'claim_acaPortal';
const COMMENTS_KEY = 'claim_comments';
const CF_PREFIX = 'claim_cf_'; // + field name

const nowPeriod = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

// ── Estilos de la ficha ─────────────────────────────────────────────────────
const INP = { width: '100%', boxSizing: 'border-box', background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 7, color: 'var(--c-text,#c8dff0)', padding: '.4rem .55rem', fontSize: '.8rem', fontFamily: 'inherit', outline: 'none' };
const LBL = { fontSize: '.6rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.07em', fontFamily: 'var(--font-mono)', marginBottom: 2 };

/** Un campo de solo lectura de la ficha. */
function Field({ label, value, mono }) {
  return (
    <div>
      <div style={LBL}>{label}</div>
      <div style={{ fontSize: '.82rem', color: 'var(--c-white,#eef6ff)', fontFamily: mono ? 'var(--font-mono)' : 'inherit', wordBreak: 'break-word' }}>{value == null || value === '' ? '—' : value}</div>
    </div>
  );
}

/**
 * Un campo editable de la ficha. Guarda solo, al salir del campo.
 *
 * ── POR QUÉ ESTOS DOS VIVEN AQUÍ FUERA Y NO DENTRO DE `ClaimsReportView` ────
 *
 * Porque un componente declarado DENTRO del cuerpo de otro es una función
 * distinta en cada repintado, y React compara los tipos por identidad: ve un
 * tipo nuevo, **desmonta el anterior y monta uno nuevo**. El `<input>` se
 * rehace desde cero y el cursor se pierde.
 *
 * En un campo controlado eso es justo el peor sitio: teclear una letra cambia
 * el estado del padre, el padre repinta, el campo se rehace... y la siguiente
 * letra ya no cae dentro. Sacarlos al módulo les da una identidad estable, que
 * es lo único que React necesita para conservar el campo entre repintados.
 *
 * `menciones`: en el recuadro de Comentarios se puede nombrar a un compañero
 * con `@` y le llega el aviso (Lissette 2026-09-12). El desplegable solo ayuda
 * a escribirlo bien — quien resuelve la mención es el servidor, al guardar.
 */
function EditField({ valor, onValor, onGuardar, usuarios, label, placeholder, mono, textarea, menciones }) {
  const conMenciones = !!(textarea && menciones);
  const men = useMenciones({ usuarios, onValor, activo: conMenciones });
  const common = {
    value: valor,
    placeholder: placeholder || '',
    onChange: (e) => onValor(e.target.value),
    onBlur: (e) => onGuardar(e.target.value),
    style: { ...INP, ...(mono ? { fontFamily: 'var(--font-mono)' } : {}) },
  };
  if (conMenciones) {
    return (
      <div>
        <div style={LBL}>{label}</div>
        {/* Relativo para que el menú del `@` se pinte pegado al recuadro. */}
        <div style={{ position: 'relative' }}>
          <textarea
            rows={2}
            {...common}
            ref={men.ref}
            onChange={men.onChange}
            onKeyDown={men.onKeyDown}
            onClick={men.onClick}
            // El guardado al salir del campo se mantiene; el menú solo añade su
            // propio cierre con retardo, para que dé tiempo al clic en una opción.
            onBlur={(e) => { men.onBlur(); common.onBlur(e); }}
            style={{ ...common.style, resize: 'vertical' }}
          />
          {men.panel}
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={LBL}>{label}</div>
      {textarea ? <textarea rows={2} {...common} style={{ ...common.style, resize: 'vertical' }} /> : <input {...common} />}
    </div>
  );
}

export function ClaimsReportView({ policies = [], clients = [], users = [], lang = 'en', onOpenClient, onPoliciesChange, commissionClientIds = new Set() }) {
  const es = lang === 'es';
  const isApiMode = typeof window !== 'undefined' && !!window.__deskteAuth;
  const editable = isApiMode && !!onPoliciesChange;
  const [typeF, setTypeF] = useState('all');
  const [carrierF, setCarrierF] = useState('all');
  const [statusF, setStatusF] = useState('all');
  const [search, setSearch] = useState('');
  // Por defecto Reclamos solo lista comisiones pendientes de cobro. El
  // interruptor existe para que las cobradas sigan siendo consultables sin
  // salir de la pantalla, no para cambiar el propósito de la lista.
  const [showPaid, setShowPaid] = useState(false);
  // A policy's client has a paid commission this year ⇒ "Comisión pagada".
  const commissionPaid = (p) => !!(p && p.clientId && commissionClientIds.has(p.clientId));
  const [open, setOpen] = useState(() => new Set()); // expanded policy ids
  // Zip code (and full DOB / dependents / spouse) are ENCRYPTED PHI — the bulk
  // client list only carries plaintext city/state, so zip arrives empty here.
  // When a card is expanded we hydrate that one client's full record on demand
  // (getClient decrypts zip) and cache it, so opening the ficha fills the zip.
  const [hydrated, setHydrated] = useState({}); // clientId -> full client
  const [hydrating, setHydrating] = useState(() => new Set()); // clientId in-flight
  const [drafts, setDrafts] = useState({}); // `${policyId}:${key}` -> string
  const [extraFields, setExtraFields] = useState([]); // custom-field names added this session (before any value saved)
  const [newFieldName, setNewFieldName] = useState('');
  const [copiedId, setCopiedId] = useState(null); // póliza cuya ficha se acaba de copiar

  const clientById = useMemo(() => Object.fromEntries((clients || []).map(c => [c.id, c])), [clients]);
  const userByName = useMemo(() => {
    const m = {};
    (users || []).forEach(u => { const n = (u.displayName || u.name || '').trim().toLowerCase(); if (n) m[n] = u; });
    return m;
  }, [users]);
  const carriers = useMemo(() => [...new Set((policies || []).map(p => p.carrier).filter(Boolean))].sort(), [policies]);
  const statusOf = (p) => (p.customStatus || p.status || '').trim();
  const statuses = useMemo(() => [...new Set((policies || []).map(statusOf).filter(Boolean))].sort(), [policies]);
  const statusLabel = (s) => String(s || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());

  // Shared custom-field names = union of every claim_cf_* key across policies,
  // plus any just added this session (so a new empty field still shows).
  const customFieldNames = useMemo(() => {
    const set = new Set(extraFields.map(s => s.trim()).filter(Boolean));
    (policies || []).forEach(p => {
      const cd = (p.customData && typeof p.customData === 'object' && !Array.isArray(p.customData)) ? p.customData : {};
      Object.keys(cd).forEach(k => { if (k.startsWith(CF_PREFIX)) { const nm = k.slice(CF_PREFIX.length); if (nm) set.add(nm); } });
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [policies, extraFields]);

  // Hydrate the full client record (to decrypt the zip) for any expanded card
  // whose client still lacks a zip. Runs per expand — one getClient call per
  // client, de-duped and cached, so zip fills in as the agent opens each ficha.
  useEffect(() => {
    if (!isApiMode) return;
    const need = new Set();
    open.forEach(pid => {
      const p = (policies || []).find(x => x.id === pid);
      const cid = p && p.clientId;
      if (!cid || hydrated[cid] || hydrating.has(cid)) return;
      const base = clientById[cid];
      if (base && (base.zipCode || base.addressZip)) return; // already has zip
      need.add(cid);
    });
    if (!need.size) return;
    setHydrating(prev => { const n = new Set(prev); need.forEach(id => n.add(id)); return n; });
    need.forEach(cid => {
      getClient(cid)
        .then(full => setHydrated(prev => ({ ...prev, [cid]: full })))
        .catch(() => {})
        .finally(() => setHydrating(prev => { const n = new Set(prev); n.delete(cid); return n; }));
    });
    /* eslint-disable-next-line */
  }, [open, isApiMode, policies, clientById]);

  // Best client record we have: the hydrated (decrypted) one wins over the
  // plaintext list row.
  const effClient = (p) => hydrated[p && p.clientId] || (p ? clientById[p.clientId] : null);

  const insuredCount = (p, c) => {
    if (Array.isArray(p.policyInsured)) return p.policyInsured.length + (Array.isArray(p.policyInsuredExtra) ? p.policyInsuredExtra.length : 0);
    return 1 + (c && c.spouse && c.spouse.firstName ? 1 : 0);
  };
  const householdCount = (c) => {
    if (!c) return '';
    const hh = Number(c.householdSize);
    if (hh > 0) return hh;
    return 1 + (c.spouse && c.spouse.firstName ? 1 : 0) + (Array.isArray(c.dependents) ? c.dependents.length : 0);
  };
  // NPN shown by default = the one that appears on the policy card, i.e. its
  // Producer Code (auto-filled from the writing agent's NPN when the policy was
  // saved). Falls back to matching the agent name → user NPN for older policies
  // that never stored a producer code (Lissette 2026-07-11).
  const npnDerivedOf = (p) => {
    if (p.producerCode && String(p.producerCode).trim()) return String(p.producerCode).trim();
    const name = (p.agentName || p.agentLabel || '').trim().toLowerCase();
    const u = name ? userByName[name] : null;
    return (u && u.npn) ? u.npn : '';
  };
  const storedOf = (p, key) => {
    const cd = (p.customData && typeof p.customData === 'object' && !Array.isArray(p.customData)) ? p.customData : {};
    return cd[key] || '';
  };
  const dKey = (id, key) => `${id}:${key}`;
  const draftVal = (p, key, fallback) => { const k = dKey(p.id, key); return drafts[k] !== undefined ? drafts[k] : fallback; };
  const setDraft = (id, key, v) => setDrafts(prev => ({ ...prev, [dKey(id, key)]: v }));

  // Persist a single customData key on the policy (merged server-side). Optimistic
  // update to global state so the value sticks when collapsing / switching tabs.
  const saveKey = async (p, key, value) => {
    const val = String(value ?? '').trim();
    if (val === storedOf(p, key)) { setDrafts(prev => { const c = { ...prev }; delete c[dKey(p.id, key)]; return c; }); return; }
    if (onPoliciesChange) {
      onPoliciesChange(prev => (prev || []).map(x => {
        if (x.id !== p.id) return x;
        const cd = { ...((x.customData && typeof x.customData === 'object' && !Array.isArray(x.customData)) ? x.customData : {}) };
        if (val) cd[key] = val; else delete cd[key];
        return { ...x, customData: cd };
      }));
    }
    setDrafts(prev => { const c = { ...prev }; delete c[dKey(p.id, key)]; return c; });
    try { await savePaymentCustomData({ policyId: p.id, customData: { [key]: val } }); }
    catch (e) { toast.error((es ? 'No se pudo guardar: ' : 'Could not save: ') + (e.message || e)); }
  };

  const addField = () => {
    const nm = newFieldName.trim();
    if (!nm) return;
    if (customFieldNames.some(f => f.toLowerCase() === nm.toLowerCase())) { toast.error(es ? 'Ese campo ya existe' : 'That field already exists'); return; }
    setExtraFields(prev => [...prev, nm]);
    setNewFieldName('');
  };

  // Los filtros de la barra (tipo / compañía / estado / búsqueda), sin mirar
  // todavía si la comisión está cobrada.
  const matchesFilters = (p) => {
    if (typeF !== 'all' && p.type !== typeF) return false;
    if (carrierF !== 'all' && (p.carrier || '') !== carrierF) return false;
    if (statusF !== 'all' && statusOf(p) !== statusF) return false;
    if (search.trim()) {
      const c = clientById[p.clientId];
      const hay = `${c ? formatClientName(c) : (p.clientName || '')} ${p.carrier || ''} ${p.memberId || ''}`.toLowerCase();
      if (!hay.includes(search.trim().toLowerCase())) return false;
    }
    return true;
  };

  // Reclamos = lo que la aseguradora aún NO le ha pagado a la agencia
  // (Lissette 2026-08-07: "solo deberían aparecer los que no me han pagado en
  // comisiones"). Una póliza con la comisión ya cobrada no es un reclamo, así
  // que sale de la lista salvo que se pida ver todas.
  // OJO: es la comisión de la agencia, NO el pago mensual del cliente — son
  // cosas distintas y esta pantalla solo mira la primera.
  const rows = useMemo(
    () => (policies || []).filter(p => matchesFilters(p) && (showPaid || !commissionPaid(p))),
    /* eslint-disable-next-line */
    [policies, typeF, carrierF, statusF, search, clientById, showPaid, commissionClientIds],
  );

  // Cuántas quedaron fuera por estar ya cobradas. Se cuenta DENTRO de los
  // mismos filtros, o el aviso mentiría al filtrar por compañía; se muestra
  // junto al interruptor para que una lista corta nunca parezca datos perdidos.
  const paidHiddenCount = useMemo(
    () => (showPaid ? 0 : (policies || []).filter(p => matchesFilters(p) && commissionPaid(p)).length),
    /* eslint-disable-next-line */
    [policies, typeF, carrierF, statusF, search, clientById, showPaid, commissionClientIds],
  );

  const toggle = (id) => setOpen(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOpen = rows.length > 0 && rows.every(p => open.has(p.id));
  const toggleAll = () => setOpen(allOpen ? new Set() : new Set(rows.map(p => p.id)));

  const exportCsv = () => {
    const base = es
      ? ['Cliente', 'Nombre', 'Apellido', 'Fecha de nacimiento', 'Compañía', 'Member ID', 'Fecha de vigencia', 'Estado', 'Tipo de póliza', 'Código postal', 'Estado (US)', 'Lead source', '# Asegurados', 'Miembros del hogar', 'Comisión', 'NPN en la aplicación', 'Portal ACA del agente', 'Comentarios']
      : ['Client', 'First name', 'Last name', 'Date of birth', 'Company', 'Member ID', 'Effective date', 'Status', 'Policy type', 'Zip code', 'State', 'Lead source', '# Insured', 'Household members', 'Commission', 'NPN on application', "Agent's ACA portal", 'Comments'];
    const head = [...base, ...customFieldNames];
    const esc = (v) => { const s = String(v == null ? '' : v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [head.map(esc).join(',')];
    rows.forEach(p => {
      const c = effClient(p);
      const pt = getPolicyType(p.type);
      lines.push([
        c ? formatClientName(c) : (p.clientName || ''), c ? (c.firstName || '') : '', c ? (c.lastName || '') : '',
        c ? (c.dob || '') : '', p.carrier || '', p.memberId || '', p.effectiveDate || '', p.customStatus || p.status || '',
        (es ? pt.labelEs : pt.label) || p.type, c ? (c.zipCode || c.addressZip || '') : '', c ? (c.state || c.addressState || '') : '',
        p.leadType || (c ? (c.leadSource || '') : ''), insuredCount(p, c), householdCount(c),
        commissionPaid(p) ? (es ? 'Pagada' : 'Paid') : (es ? 'No pagada' : 'Not paid'),
        storedOf(p, NPN_KEY) || npnDerivedOf(p), storedOf(p, ACA_KEY), storedOf(p, COMMENTS_KEY),
        ...customFieldNames.map(nm => storedOf(p, CF_PREFIX + nm)),
      ].map(esc).join(','));
    });
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `reclamos-${nowPeriod()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(es ? 'Exportado a CSV' : 'Exported to CSV');
  };

  // ── Copiar los campos de UN reclamo ───────────────────────────────────────
  // Lissette, 2026-09-12: *"en comisiones en la parte de reclamo puedes colocar
  // al lado la opción de copiar los campos"*. El CSV de arriba es para la lista
  // entera; esto es para pegar UNA ficha en el correo a la aseguradora o en su
  // portal sin ir campo por campo.
  //
  // Se copia EXACTAMENTE lo que se ve al desplegar la ficha: los mismos
  // títulos, el mismo orden y el idioma de la pantalla. Si el texto copiado no
  // coincide con lo que hay delante, deja de servir para comprobar nada.
  const fichaComoTexto = (p, c) => {
    const pt = getPolicyType(p.type);
    // Los campos editables se copian COMO ESTÁN EN PANTALLA: si alguien acaba
    // de escribir un comentario y todavía no ha salido del campo, el borrador
    // es lo que hay delante, no lo último guardado.
    const vivo = (k) => draftVal(p, k, storedOf(p, k));
    const secciones = [
      [es ? 'Cliente' : 'Client', [
        [es ? 'Nombre completo' : 'Full name', c ? formatClientName(c) : (p.clientName || '')],
        [es ? 'F. Nacimiento' : 'Date of birth', c && c.dob ? fmt(c.dob) : ''],
        [es ? 'Código postal' : 'Zip code', c ? (c.zipCode || c.addressZip) : ''],
        [es ? 'Estado (US)' : 'State', c ? (c.state || c.addressState) : ''],
        ['Lead source', p.leadType || (c ? c.leadSource : '')],
        [es ? '# Asegurados' : '# Insured', insuredCount(p, c)],
        [es ? 'Miembros del hogar' : 'Household members', householdCount(c)],
      ]],
      [es ? 'Póliza' : 'Policy', [
        [es ? 'Compañía' : 'Company', p.carrier],
        ['Member ID', p.memberId],
        [es ? 'F. Vigencia' : 'Effective date', p.effectiveDate ? fmt(p.effectiveDate) : ''],
        [es ? 'Tipo de póliza' : 'Policy type', (es ? pt.labelEs : pt.label) || p.type],
        [es ? 'Estado' : 'Status', p.customStatus || p.status],
        [es ? 'Comisión' : 'Commission', commissionPaid(p) ? (es ? 'Pagada' : 'Paid') : (es ? 'No pagada' : 'Not paid')],
      ]],
      [es ? 'Datos del reclamo' : 'Claim details', [
        [es ? 'NPN en la aplicación' : 'NPN on application', vivo(NPN_KEY) || npnDerivedOf(p)],
        [es ? 'Portal ACA del agente' : "Agent's ACA portal", vivo(ACA_KEY)],
        [es ? 'Comentarios' : 'Comments', vivo(COMMENTS_KEY)],
      ]],
      [es ? 'Campos personalizados' : 'Custom fields',
        customFieldNames.map(nm => [nm, vivo(CF_PREFIX + nm)])],
    ];
    const titulo = [c ? formatClientName(c) : (p.clientName || ''), p.carrier, (es ? pt.labelEs : pt.label) || p.type]
      .map(s => String(s == null ? '' : s).trim()).filter(Boolean).join(' · ');
    const partes = [(es ? 'RECLAMO' : 'CLAIM') + (titulo ? ' — ' + titulo : '')];
    secciones.forEach(([nombre, campos]) => {
      // Un campo vacío no se escribe: una ficha pegada llena de rayas es ruido,
      // y una sección entera vacía no aporta ni el título.
      const lineas = campos
        .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
        .map(([k, v]) => `${k}: ${String(v).trim()}`);
      if (lineas.length) partes.push(`\n${nombre}\n${lineas.join('\n')}`);
    });
    return partes.join('\n');
  };

  const copiarFicha = async (p) => {
    // El código postal es PHI y viaja cifrado: solo se descifra al pedir la
    // ficha completa del cliente, y eso hoy pasa al DESPLEGAR la tarjeta. Como
    // este botón está en la cabecera y se puede pulsar con la tarjeta cerrada,
    // se pide aquí también — copiar una ficha sin el código postal y no decirlo
    // es peor que esperar medio segundo.
    let c = effClient(p);
    if (isApiMode && p.clientId && !hydrated[p.clientId] && !(c && (c.zipCode || c.addressZip))) {
      try {
        const full = await getClient(p.clientId);
        if (full) { c = full; setHydrated(prev => ({ ...prev, [p.clientId]: full })); }
      } catch { /* sin la ficha completa se copia lo que ya hay a la vista */ }
    }
    try {
      await navigator.clipboard.writeText(fichaComoTexto(p, c));
      setCopiedId(p.id);
      setTimeout(() => setCopiedId(null), 1600);
      toast.success(es ? 'Campos del reclamo copiados' : 'Claim fields copied');
    } catch {
      toast.error(es ? 'No se pudo copiar' : 'Could not copy');
    }
  };

  // ── styles ────────────────────────────────────────────────────────────────
  const soft = 'var(--c-text2,#7aa3c0)';
  const inp = INP;
  const lbl = LBL;

  // Lo que necesita un campo editable de esta ficha. Va aparte porque `EditField`
  // vive fuera del componente (ver su cabecera: dentro, React lo remontaba en
  // cada repintado y el campo perdía el cursor a la primera letra).
  const campo = (p, k) => ({
    valor: draftVal(p, k, storedOf(p, k)),
    onValor: (v) => setDraft(p.id, k, v),
    onGuardar: (v) => saveKey(p, k, v),
    usuarios: users,
  });

  const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '.7rem .9rem' };

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <div className="dkt-sec-title" style={{ margin: 0 }}>
          <FileText size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
          {es ? 'Reclamos' : 'Claims'}
        </div>
        <div className="dkt-sec-sub">
          {es
            ? 'Comisiones que la aseguradora todavía NO te ha pagado este año — no tiene nada que ver con el pago mensual del cliente. Una ficha desplegable por póliza con los datos del cliente principal; los campos editables y los personalizados se guardan solos.'
            : 'Commissions the carrier has NOT paid you yet this year — unrelated to the client\'s monthly premium. One expandable card per policy with the primary client\'s details; editable and custom fields auto-save.'}
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', marginBottom: '.75rem', alignItems: 'flex-end' }}>
        <Select label={es ? 'Tipo de póliza' : 'Policy type'} value={typeF} onChange={e => setTypeF(e.target.value)}>
          <option value="all">{es ? 'Todos los tipos' : 'All types'}</option>
          {POLICY_TYPES.map(pt => <option key={pt.id} value={pt.id}>{(pt.icon ? pt.icon + ' ' : '') + (es ? pt.labelEs : pt.label)}</option>)}
        </Select>
        <Select label={es ? 'Compañía' : 'Company'} value={carrierF} onChange={e => setCarrierF(e.target.value)}>
          <option value="all">{es ? 'Todas' : 'All'}</option>
          {carriers.map(c => <option key={c} value={c}>{c}</option>)}
        </Select>
        <Select label={es ? 'Estado' : 'Status'} value={statusF} onChange={e => setStatusF(e.target.value)}>
          <option value="all">{es ? 'Todos' : 'All'}</option>
          {statuses.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </Select>
        <div>
          <div style={lbl}>{es ? 'Buscar' : 'Search'}</div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={es ? 'cliente, compañía, member id…' : 'client, company, member id…'} style={{ ...inp, minWidth: 200 }} />
        </div>
        <label
          style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem', fontSize: '.72rem', color: soft, cursor: 'pointer', paddingBottom: 6 }}
          title={es ? 'Reclamos muestra solo las comisiones que aún no te han pagado' : 'Claims lists only commissions not yet paid to you'}
        >
          <input type="checkbox" checked={showPaid} onChange={e => setShowPaid(e.target.checked)} />
          {es ? 'Ver también las ya cobradas' : 'Also show already paid'}
          {paidHiddenCount > 0 && (
            <span style={{ opacity: 0.75 }}>({paidHiddenCount} {es ? 'ocultas' : 'hidden'})</span>
          )}
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '.5rem' }}>
          {rows.length > 0 && <button onClick={toggleAll} className="dkt-btn ghost xs">{allOpen ? (es ? 'Colapsar todo' : 'Collapse all') : (es ? 'Expandir todo' : 'Expand all')}</button>}
          <button onClick={exportCsv} className="dkt-btn ghost xs" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Download size={13} />{es ? 'Exportar Excel/CSV' : 'Export Excel/CSV'}
          </button>
        </div>
      </div>

      {rows.length === 0 && (
        <div className="dkt-card" style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--c-text3,#45688a)' }}>
          {/* "Nada que reclamar" y "los filtros no dejan pasar nada" se leen
              igual si el texto es genérico — y el primero es una buena noticia. */}
          {paidHiddenCount > 0
            ? (es
                ? `Sin comisiones pendientes de cobro. Las ${paidHiddenCount} pólizas de esta vista ya tienen su comisión pagada.`
                : `No commissions pending. All ${paidHiddenCount} policies in this view have already been paid.`)
            : (es ? 'No hay pólizas para estos filtros' : 'No policies for these filters')}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
        {rows.map(p => {
          const c = effClient(p);
          const zipLoading = p.clientId && hydrating.has(p.clientId) && !(c && (c.zipCode || c.addressZip));
          const pt = getPolicyType(p.type);
          const cname = c ? formatClientName(c) : (p.clientName || '—');
          const commPaid = commissionPaid(p);
          const isOpen = open.has(p.id);
          return (
            <div key={p.id} className="dkt-card" style={{ padding: 0, overflow: 'hidden' }}>
              {/* header */}
              <div onClick={() => toggle(p.id)} style={{ display: 'flex', alignItems: 'center', gap: '.7rem', padding: '.7rem .9rem', cursor: 'pointer', background: isOpen ? 'var(--c-surface2,#112538)' : 'transparent' }}>
                {isOpen ? <ChevronDown size={16} style={{ color: soft, flexShrink: 0 }} /> : <ChevronRight size={16} style={{ color: soft, flexShrink: 0 }} />}
                <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', background: (pt.color || '#3b82f6') + '22' }}>{pt.icon || '📄'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '.9rem', color: 'var(--c-white,#eef6ff)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cname}</div>
                  <div style={{ fontSize: '.7rem', color: soft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {(p.carrier || '—')} · {(es ? pt.labelEs : pt.label) || p.type}{p.memberId ? ' · ' + p.memberId : ''}
                  </div>
                </div>
                <span style={{ fontSize: '.68rem', padding: '.15rem .5rem', borderRadius: 6, background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', color: soft, whiteSpace: 'nowrap' }}>{p.customStatus || p.status || '—'}</span>
                {commPaid
                  ? <span style={{ fontSize: '.68rem', fontWeight: 700, padding: '.15rem .5rem', borderRadius: 6, background: 'var(--c-green-dim,rgba(0,255,136,.1))', color: 'var(--c-green,#00ff88)', whiteSpace: 'nowrap' }}>{es ? 'Comisión pagada' : 'Commission paid'}</span>
                  : <span style={{ fontSize: '.68rem', fontWeight: 700, padding: '.15rem .5rem', borderRadius: 6, background: 'var(--c-red-dim,rgba(255,61,107,.12))', color: 'var(--c-red,#ff3d6b)', whiteSpace: 'nowrap' }}>⚠ {es ? 'Comisión no pagada' : 'Commission not paid'}</span>}
                {/* Copiar los campos, al lado del reclamo. `stopPropagation`
                    porque toda la cabecera despliega la tarjeta: copiar no
                    debe abrirla ni cerrarla. */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); copiarFicha(p); }}
                  className="dkt-btn ghost xs"
                  title={es ? 'Copiar los campos de este reclamo' : "Copy this claim's fields"}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  {copiedId === p.id
                    ? <><Check size={12} style={{ color: 'var(--c-green,#00ff88)' }} />{es ? 'Copiado' : 'Copied'}</>
                    : <><Copy size={12} />{es ? 'Copiar' : 'Copy'}</>}
                </button>
              </div>

              {/* body */}
              {isOpen && (
                <div style={{ padding: '.9rem', borderTop: '1px solid var(--c-border,#1a3550)', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <section>
                    <div style={{ ...lbl, color: 'var(--c-cyan,#00d4ff)', marginBottom: 8 }}>{es ? 'Cliente' : 'Client'}</div>
                    <div style={gridStyle}>
                      <div>
                        <div style={lbl}>{es ? 'Nombre completo' : 'Full name'}</div>
                        {onOpenClient && c
                          ? <button type="button" onClick={() => onOpenClient(c)} title={es ? 'Abrir ficha del cliente' : 'Open client'} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--c-cyan,#00d4ff)', fontWeight: 600, fontSize: '.82rem', fontFamily: 'inherit' }}>{cname}<ExternalLink size={11} style={{ opacity: .8 }} /></button>
                          : <div style={{ fontSize: '.82rem', color: 'var(--c-white,#eef6ff)' }}>{cname}</div>}
                      </div>
                      <Field label={es ? 'F. Nacimiento' : 'Date of birth'} value={c && c.dob ? fmt(c.dob) : ''} />
                      <Field label={es ? 'Código postal' : 'Zip code'} value={zipLoading ? '…' : (c ? (c.zipCode || c.addressZip) : '')} />
                      <Field label={es ? 'Estado (US)' : 'State'} value={c ? (c.state || c.addressState) : ''} />
                      <Field label="Lead source" value={p.leadType || (c ? c.leadSource : '')} />
                      <Field label={es ? '# Asegurados' : '# Insured'} value={insuredCount(p, c)} />
                      <Field label={es ? 'Miembros del hogar' : 'Household members'} value={householdCount(c)} />
                    </div>
                  </section>

                  <section>
                    <div style={{ ...lbl, color: 'var(--c-cyan,#00d4ff)', marginBottom: 8 }}>{es ? 'Póliza' : 'Policy'}</div>
                    <div style={gridStyle}>
                      <Field label={es ? 'Compañía' : 'Company'} value={p.carrier} />
                      <Field label="Member ID" value={p.memberId} mono />
                      <Field label={es ? 'F. Vigencia' : 'Effective date'} value={p.effectiveDate ? fmt(p.effectiveDate) : ''} />
                      <Field label={es ? 'Tipo de póliza' : 'Policy type'} value={(es ? pt.labelEs : pt.label) || p.type} />
                      <Field label={es ? 'Estado' : 'Status'} value={p.customStatus || p.status} />
                      <div>
                        <div style={lbl}>{es ? 'Comisión' : 'Commission'}</div>
                        <div style={{ fontSize: '.82rem', fontWeight: 600, color: commPaid ? 'var(--c-green,#00ff88)' : 'var(--c-red,#ff3d6b)' }}>
                          {commPaid ? (es ? 'Pagada' : 'Paid') : (es ? 'No pagada' : 'Not paid')}
                        </div>
                      </div>
                    </div>
                  </section>

                  <section>
                    <div style={{ ...lbl, color: 'var(--c-cyan,#00d4ff)', marginBottom: 8 }}>{es ? 'Datos del reclamo' : 'Claim details'}</div>
                    {editable ? (
                      <div style={gridStyle}>
                        <EditField {...campo(p, NPN_KEY)} label={es ? 'NPN en la aplicación' : 'NPN on application'} placeholder={npnDerivedOf(p)} mono />
                        <EditField {...campo(p, ACA_KEY)} label={es ? 'Portal ACA del agente' : "Agent's ACA portal"} />
                        <div style={{ gridColumn: '1 / -1' }}>
                          <EditField {...campo(p, COMMENTS_KEY)} label={es ? 'Comentarios' : 'Comments'} textarea menciones />
                        </div>
                      </div>
                    ) : (
                      <div style={gridStyle}>
                        <Field label={es ? 'NPN en la aplicación' : 'NPN on application'} value={storedOf(p, NPN_KEY) || npnDerivedOf(p)} mono />
                        <Field label={es ? 'Portal ACA del agente' : "Agent's ACA portal"} value={storedOf(p, ACA_KEY)} />
                        <div style={{ gridColumn: '1 / -1' }}><Field label={es ? 'Comentarios' : 'Comments'} value={storedOf(p, COMMENTS_KEY)} /></div>
                      </div>
                    )}
                  </section>

                  <section>
                    <div style={{ ...lbl, color: 'var(--c-cyan,#00d4ff)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {es ? 'Campos personalizados' : 'Custom fields'}
                      {editable && <span style={{ color: 'var(--c-text3,#45688a)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{es ? '— iguales para todos los reclamos' : '— shared across all claims'}</span>}
                    </div>
                    {customFieldNames.length === 0 && !editable && <div style={{ fontSize: '.75rem', color: soft }}>{es ? 'Sin campos personalizados.' : 'No custom fields.'}</div>}
                    <div style={gridStyle}>
                      {customFieldNames.map(nm => (
                        editable
                          ? <EditField key={nm} {...campo(p, CF_PREFIX + nm)} label={nm} />
                          : <Field key={nm} label={nm} value={storedOf(p, CF_PREFIX + nm)} />
                      ))}
                    </div>
                    {editable && (
                      <div style={{ display: 'flex', gap: '.4rem', marginTop: '.7rem', alignItems: 'center' }}>
                        <input value={newFieldName} onChange={e => setNewFieldName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addField(); }} placeholder={es ? 'Nombre del nuevo campo…' : 'New field name…'} style={{ ...inp, maxWidth: 240 }} />
                        <button onClick={addField} className="dkt-btn ghost xs" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Plus size={13} />{es ? 'Agregar campo' : 'Add field'}</button>
                      </div>
                    )}
                  </section>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: '.6rem' }}>
        {es
          ? `${rows.length} póliza(s). "Comisión" = si la aseguradora ya pagó la comisión de ese cliente este año (de Comisiones, no del pago del cliente). "Copiar" deja los campos de ese reclamo en el portapapeles, listos para pegar. El código postal es dato protegido (PHI): se carga al abrir la ficha (o al copiarla). El NPN muestra por defecto el de la póliza (Código Productor) hasta que escribas uno. Un campo personalizado nuevo queda guardado en cuanto le escribes un valor en algún reclamo.`
          : `${rows.length} policy(ies). "Commission" = whether the carrier has already paid that client's commission this year (from Commissions, not the client's payment). "Copy" puts that claim's fields on the clipboard, ready to paste. Zip code is protected data (PHI): it loads when you open the card (or copy it). NPN defaults to the policy's Producer Code until you type one. A new custom field is saved once you give it a value on any claim.`}
      </div>
    </div>
  );
}
